import { useMemo, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import type { ViewScope } from '../navigation/context'
import { useRead } from '../navigation/useRead'
import { ErrorState } from '../state'
import SourceQuestionReview from './SourceQuestionReview'
import { parseRecoveryItem, recoveryQuery } from './recovery'
import { sourceQuestionBelongsToCard } from './documentSourceQuestions'

interface Props {
  taskIds: readonly string[]
  documentIds: readonly string[]
  scope: ViewScope
  revision: string
  onChanged: () => void
}

/** Retained questions share the document card; they do not become Book selections. */
export default function DocumentSourceQuestions(props: Props) {
  const session = useAuthSession()
  return (
    <section aria-label="Held source items">
      <h3>Source details to review</h3>
      {props.taskIds.map((taskId) => (
        <SourceQuestionOnCard
          key={`${session}:${taskId}:${props.revision}:${JSON.stringify(props.scope)}`}
          {...props}
          taskId={taskId}
        />
      ))}
    </section>
  )
}

function SourceQuestionOnCard({
  taskId,
  documentIds,
  scope,
  onChanged,
}: Props & { taskId: string }) {
  const [reload, setReload] = useState(0)
  const read = useRead<unknown>(recoveryQuery(scope, { taskId }), reload)
  const parsed = useMemo(() => {
    if (read.loading) return {}
    if (read.error) return { error: read.error }
    try {
      const item = parseRecoveryItem(read.value, scope, taskId)
      if (!sourceQuestionBelongsToCard(item, taskId, documentIds))
        throw new Error('The held item is no longer current on this document review.')
      return { item }
    } catch (error) {
      return { error }
    }
  }, [read.value, read.loading, read.error, scope, taskId, documentIds])
  if (read.loading) return <p role="status">Reading held source details…</p>
  if (parsed.error)
    return (
      <ErrorState
        compact
        what="held source details"
        error={parsed.error}
        onRetry={() => setReload((value) => value + 1)}
      />
    )
  return parsed.item ? (
    <SourceQuestionReview item={parsed.item} scope={scope} refresh={0} onChanged={onChanged} />
  ) : null
}
